// SignUp.jsx - User registration page matching Figma design
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyPathLanguage, supportedLanguageCodes } from '../i18n';
import { useUser } from '../context/UserContext';
import { ROUTES, localePath, parseLocalePath } from '../constants/Routes';
import AuthLayout from '../components/auth/AuthLayout';
import { AUTH_LINK_CLASS } from '../components/auth/authLinkStyles';
import AuthHeader from '../components/auth/AuthHeader';
import TextField from '../components/auth/TextField';
import PasswordField from '../components/auth/PasswordField';
import Checkbox from '../components/auth/Checkbox';
import PrimaryButton from '../components/auth/PrimaryButton';
import SocialButtons from '../components/auth/SocialButtons';

// Simple PageTitle component
const PageTitle = ({ title }) => {
  React.useEffect(() => {
    document.title = title || 'Bloom IQ';
  }, [title]);
  return null;
};

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // Routes use literal `/en/signup` / `/es/signup` paths, not a `:lang`
  // dynamic param — derive locale from the pathname.
  const { locale: lang } = parseLocalePath(location.pathname);
  const navigate = useNavigate();
  const { register } = useUser();
  const [error, setError] = useState('');

  // Honour the URL locale (e.g. /es/signup) so the form renders in the
  // visitor's marketing-site language and the same value is forwarded to
  // register() → create_user_db, creating the Firestore user doc with
  // language already set.
  const signupLanguage = supportedLanguageCodes.includes(lang) ? lang : null;

  useEffect(() => {
    applyPathLanguage(location.pathname);
  }, [location.pathname]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('errors.nameRequired');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('errors.nameMinLength');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = t('errors.nameOnlyLetters');
    }

    // Surname validation
    if (!formData.surname.trim()) {
      newErrors.surname = t('errors.surnameRequired');
    } else if (formData.surname.trim().length < 2) {
      newErrors.surname = t('errors.surnameMinLength');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.surname)) {
      newErrors.surname = t('errors.surnameOnlyLetters');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = t('errors.emailRequired');
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t('errors.enterValidEmail');
    }

    // Confirm email validation
    if (!formData.confirmEmail) {
      newErrors.confirmEmail = t('errors.confirmEmailRequired');
    } else if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = t('errors.emailsNoMatch');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('errors.passwordMinLength');
    } else if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = t('errors.passwordRequirements');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.passwordsNoMatch');
    }

    // Terms acceptance
    if (!acceptedTerms) {
      newErrors.terms = t('errors.acceptTermsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.surname,
        signupLanguage || i18n.language,
      );
      // Registration complete — redirect to the success/thank-you page.
      // The user is already signed out (see UserContext.register).
      navigate(ROUTES.SIGN_UP_SUCCESS);
    } catch (error) {
      console.error('Registration error:', error);
      // Handle Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        setError(t('errors.emailAlreadyInUse'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('errors.invalidEmail'));
      } else if (error.code === 'auth/weak-password') {
        setError(t('errors.weakPassword'));
      } else {
        setError(t('errors.registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <PageTitle title={`${t('auth.signUp')} - Bloom IQ`} />
      <AuthLayout variant="light">
        <div className="w-full">
          {/* Logo and Title */}
          <AuthHeader
            title={t('auth.register')}
            subtitle={t('auth.registerSubtitle')}
          />

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-5 text-sm">
              {error}
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <TextField
              variant="light"
              label={t('auth.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              required
              autoComplete="given-name"
            />

            <TextField
              variant="light"
              label={t('auth.surname')}
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              error={errors.surname}
              required
              autoComplete="family-name"
            />

            <TextField
              variant="light"
              label={t('auth.email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
              autoComplete="email"
            />

            <TextField
              variant="light"
              label={t('auth.confirmEmail')}
              type="email"
              name="confirmEmail"
              value={formData.confirmEmail}
              onChange={handleChange}
              error={errors.confirmEmail}
              required
              autoComplete="email"
            />

            <PasswordField
              variant="light"
              label={t('auth.password')}
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              autoComplete="new-password"
            />

            <PasswordField
              variant="light"
              label={t('auth.confirmPassword')}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
              autoComplete="new-password"
            />

            {/* Terms and Conditions Checkbox */}
            <div className="mb-[24px]">
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                name="terms"
                label={
                  <span className="text-[14px] text-[#282A29]">
                    {t('auth.acceptTerms')}{' '}
                    {/* Opens in a new tab so a half-filled form is not lost. */}
                    <a
                      href={ROUTES.LEGAL_TERMS}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={AUTH_LINK_CLASS}
                    >
                      {t('auth.termsAndConditions')}
                    </a>
                  </span>
                }
                error={errors.terms}
              />
            </div>

            {/* Submit Button */}
            <PrimaryButton type="submit" loading={loading} disabled={loading}>
              {t('auth.signUp')}
            </PrimaryButton>

            {/* Already have account link */}
            <div className="text-center text-[#5D625F] text-sm font-['Inter']">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                to={localePath(lang, '/login')}
                className={AUTH_LINK_CLASS}
              >
                {t('auth.login')}
              </Link>
            </div>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#C3C4C3]"></div>
            <span className="text-[#5D625F] text-[16px] font-['Inter']">{t('common.or')}</span>
            <div className="flex-1 h-px bg-[#C3C4C3]"></div>
          </div>

          {/* Social Login Buttons */}
          <SocialButtons
            variant="light"
            onError={setError}
            successRoute={ROUTES.BLOOMTERMINAL}
          />
        </div>
      </AuthLayout>
    </>
  );
};

export default SignUp;
